import React from 'react';
import { toast } from 'react-toastify';
import SVG from 'react-inlinesvg';
import { checkCircleSVG } from '../../shared/helpers/icons';
import styles from '../../shared/styles/notifications.module.scss';
import { takeLatest, put, call, select, debounce, delay } from "redux-saga/effects";

import {
    GET_DEVICES_REQUEST,
    getDevicesSuccess,
    getDevicesFailure,
    GET_DEVICES_STATUSES_REQUEST,
    getDevicesStatusesSuccess,
    getDevicesStatusesFailure,
    GET_DEVICES_SUBSTATUSES_REQUEST,
    getDevicesSubstatusesSuccess,
    getDevicesSubstatusesFailure,
    SUBMIT_DEVICES_BATCH_REQUEST,
    submitDevicesBatchSuccess,
    submitDevicesBatchFailure,
    setStateDevices,
    SUBMIT_DEVICES_REQUEST,
    submitDevicesSuccess,
    submitDevicesFailure,
    GET_TYPING_DEVICES_REQUEST,
    SUBMIT_DEVICES_STATUSES_REQUEST,
    submitDevicesStatusesSuccess,
    submitDevicesStatusesFailure,
    SUBMIT_DEVICES_VALIDATE_BATCH_REQUEST,
    submitDevicesValidateBatchSuccess,
    submitDevicesValidateBatchFailure,
    GET_EXPORT_DEVICES_REQUEST,
    getExportDevicesFailure,
    getExportDevicesSuccess,
    GET_HISTORY_DEVICES_REQUEST,
    getHistoryDevicesSuccess,
    getHistoryDevicesFailure,
    PATCH_DEVICES_REQUEST,
    patchDevicesSuccess,
    patchDevicesFailure
} from "../actions/devices";
import {
    getHistoryDevices,
    getDevices,
    getDevicesStatuses,
    getDevicesSubstatuses,
    postBatchDevices,
    putDevice,
    postDevicesStatuses,
    putDevicesStatuses,
    postValidateBatchDevices,
    getExportDevicesService,
    patchDevice
} from "../services/devices";
import { pingEventBeforeSubmitBatch } from "../../shared/sagas/events";
import { showGlobalError } from "../../app/actions/app";
import { accessTokenSelector } from "../../auth/selectors/token";

const getDevicesWorker = function* ({ payload: { filters } }) {
    try {
        const token = yield select(accessTokenSelector);

        const {
            data: { devices, total }
        } = yield call(getDevices, filters, token);
        
        const isFirstPage = !filters.page || filters.page === 1;
        
        yield put(getDevicesSuccess(devices, total, isFirstPage));
    } catch (err) {
        yield put(getDevicesFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getDevicesStatusesWorker = function* ({ payload: { filters } }) {
    try {
        const token = yield select(accessTokenSelector);
        const {
            data: { statuses, total }
        } = yield call(getDevicesStatuses, filters, token);

        yield put(getDevicesStatusesSuccess(statuses, total));
    } catch (err) {
        yield put(getDevicesStatusesFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getDevicesSubstatusesWorker = function* ({ payload: { filters } }) {
    try {
      const token = yield select(accessTokenSelector);
      const response = yield call(getDevicesSubstatuses, filters, token);
      const { data } = response;
  
      const substatuses = data?.substatus || data?.substatuses || [];
      const total = data?.total || 0;
  
      yield put(getDevicesSubstatusesSuccess(substatuses, total));
    } catch (err) {
      yield put(getDevicesSubstatusesFailure(err));
      yield put(showGlobalError(true, err));
    }
  };
  

const getHistoryDevicesWorker = function* ({ payload: { filters } }) {
    try {
        const token = yield select(accessTokenSelector);
        const { imei } = filters;
        const {
            data: { history }
        } = yield call(getHistoryDevices, imei, token);

        yield put(getHistoryDevicesSuccess(history));
    } catch (err) {
        yield put(getHistoryDevicesFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const submitDevicesBatchWorker = function* ({ payload: { device, options = {} } }) {
    try {
        const token = yield select(accessTokenSelector);

        const {
            data: { event }
        } = yield call(postBatchDevices, device, token);

        // Esperar a que el evento termine y marcar como listo para PATCH
        if (event && event._id) {
            try {
                yield call(pingEventBeforeSubmitBatch, event._id, token);
                // Una vez que pingEventBeforeSubmitBatch termina con éxito, la carga está lista
                yield put(setStateDevices({ isReadyForPatch: true }));
            } catch (pingError) {
                console.error('Error en pingEventBeforeSubmitBatch:', pingError);
                yield put(setStateDevices({ isReadyForPatch: false }));
            }
        }

        yield put(submitDevicesBatchSuccess());

        if (!options.suppressSuccessMessage) {
            toast.success(
                <div className={styles.customToastSuccess}>
                    <div className={styles.toastContent}>
                        <SVG src={checkCircleSVG} className={styles.toastIconSuccess} />
                       Archivo subido con éxito
                    </div>
                </div>,
                {
                    className: styles.toast,
                    toastId: `user-created-${Date.now()}`
                }
            );
        }

        if (!options.skipReset) {
            yield put(setStateDevices({ submitSuccess: false }));
        }
    } catch (err) {
        yield put(submitDevicesBatchFailure());
        yield put(showGlobalError(true, err, err.key));
        yield put(setStateDevices({ isReadyForPatch: false }));
    }
};

const submitDevicesValidateBatchWorker = function* ({ payload: { device } }) {
    try {

        const token = yield select(accessTokenSelector);

        const {
            data: { validations }
        } = yield call(postValidateBatchDevices, device, token);

        yield put(submitDevicesValidateBatchSuccess(validations));
        yield put(setStateDevices({ submitValidateBatchSuccess: false }));

    } catch (err) {
        yield put(submitDevicesValidateBatchFailure());
        yield put(showGlobalError(true, err, err.key));
    }
};

const submitDevicesWorker = function* ({ payload: { device } }) {
    try {
        const token = yield select(accessTokenSelector);

        yield call(putDevice, device, token);

        yield put(submitDevicesSuccess());
        yield put(setStateDevices({ submitSuccess: false }));
    } catch (err) {
        yield put(submitDevicesFailure());
        yield put(showGlobalError(true, err, err.key));
    }
};

const submitDevicesStatusWorker = function* ({ payload: { status } }) {
    try {
        const token = yield select(accessTokenSelector);

        if (!status._id) {
            yield call(postDevicesStatuses, status, token);
            yield put(showGlobalError(true, 200, "created"));
        } else {
            yield call(putDevicesStatuses, status, token);
            yield put(showGlobalError(true, 200, "updated"));
        }

        yield put(submitDevicesStatusesSuccess());
    } catch (err) {
        yield put(submitDevicesStatusesFailure());
        yield put(showGlobalError(true, err));
    }
};

const getExportDevicesWorker = function* ({ payload: { filters }}) {
    try {
        const accessToken = yield select(accessTokenSelector);

        const response = yield call(getExportDevicesService, filters, accessToken);

        // El API retorna directamente el archivo Excel, no un evento
        if (response.data && response.status === 200) {

            // Crear blob y descargar archivo
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `devices_export_${new Date().toISOString().slice(0,10)}.xlsx`);
            document.body.appendChild(link);
            link.click();

            // Limpiar
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

            yield put(getExportDevicesSuccess({ success: true }));
        } else {
            yield put(getExportDevicesFailure());
        }
    } catch (err) {
        yield put(getExportDevicesFailure());
        yield put(showGlobalError(true, err));
    }
}

const patchDevicesWorker = function* ({ payload: { device } }) {
    try {
      const token = yield select(accessTokenSelector);
      
      yield call(patchDevice, device, token);
  
      yield put(patchDevicesSuccess());
      toast.success(
        <div className={styles.customToastSuccess}>
            <div className={styles.toastContent}>
                <SVG src={checkCircleSVG} className={styles.toastIconSuccess} />
               Dispositivos actualizados con éxito
            </div>
        </div>,
        {
            className: styles.toast,
            toastId: `user-created-${Date.now()}`
        }
    );
      yield put(setStateDevices({ submitSuccess: false }));
    } catch (err) {
      yield put(patchDevicesFailure());
      yield put(showGlobalError(true, err, err.key));
    }
  };

export default [
    takeLatest(GET_DEVICES_REQUEST, getDevicesWorker),
    debounce(500, GET_TYPING_DEVICES_REQUEST, getDevicesWorker),
    takeLatest(GET_DEVICES_STATUSES_REQUEST, getDevicesStatusesWorker),
    takeLatest(GET_DEVICES_SUBSTATUSES_REQUEST, getDevicesSubstatusesWorker),
    takeLatest(GET_HISTORY_DEVICES_REQUEST, getHistoryDevicesWorker),
    takeLatest(SUBMIT_DEVICES_BATCH_REQUEST, submitDevicesBatchWorker),
    takeLatest(SUBMIT_DEVICES_REQUEST, submitDevicesWorker),
    takeLatest(SUBMIT_DEVICES_STATUSES_REQUEST, submitDevicesStatusWorker),
    takeLatest(SUBMIT_DEVICES_VALIDATE_BATCH_REQUEST, submitDevicesValidateBatchWorker),
    takeLatest(GET_EXPORT_DEVICES_REQUEST, getExportDevicesWorker),
    takeLatest(PATCH_DEVICES_REQUEST, patchDevicesWorker)
];
